import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Eye, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentStyles = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [styles, setStyles] = useState({
    font_family: 'Calibri',
    font_family_headings: 'Calibri',
    font_size_title: 18,
    font_size_heading_1: 14,
    font_size_heading_2: 12,
    font_size_normal: 11,
    font_size_table: 10,
    font_size_small: 9,
    font_size_footer: 8,
    color_primary: '#335C2E',
    color_secondary: '#C7A027',
    color_text: '#333333',
    color_text_light: '#666666',
    space_before_title: 12,
    space_after_title: 6,
    space_before_heading: 12,
    space_after_heading: 6,
    space_between_paragraphs: 6,
    line_spacing: 1.15
  });

  const [defaultStyles] = useState({ ...styles });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStyles();
  }, [navigate]);

  const fetchStyles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.document_styles) {
        setStyles(response.data.document_styles);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des styles');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/settings`, 
        { document_styles: styles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Styles enregistrés avec succès!');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Voulez-vous vraiment réinitialiser tous les styles aux valeurs par défaut ?')) {
      setStyles({ ...defaultStyles });
      toast.info('Styles réinitialisés (non sauvegardés)');
    }
  };

  const fonts = [
    'Calibri',
    'Arial',
    'Times New Roman',
    'Georgia',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Courier New',
    'Helvetica'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#335C2E] mx-auto"></div>
          <p className="mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold" style={{ color: '#335C2E' }}>
              Configuration des Styles de Documents
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-100"
            >
              <RotateCcw size={18} />
              Réinitialiser
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-white rounded"
              style={{ background: '#335C2E' }}
            >
              <Save size={18} />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Configuration */}
          <div className="space-y-6">
            {/* Polices */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
                🔤 Polices
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Police principale</label>
                  <select
                    value={styles.font_family}
                    onChange={(e) => setStyles({ ...styles, font_family: e.target.value })}
                    className="w-full p-2 border rounded"
                    style={{ fontFamily: styles.font_family }}
                  >
                    {fonts.map(font => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Police des titres</label>
                  <select
                    value={styles.font_family_headings}
                    onChange={(e) => setStyles({ ...styles, font_family_headings: e.target.value })}
                    className="w-full p-2 border rounded"
                    style={{ fontFamily: styles.font_family_headings }}
                  >
                    {fonts.map(font => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tailles */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
                📏 Tailles de Police (pt)
              </h2>
              <div className="space-y-3">
                {[
                  { key: 'font_size_title', label: 'Titre principal', min: 14, max: 28 },
                  { key: 'font_size_heading_1', label: 'Titre niveau 1', min: 12, max: 20 },
                  { key: 'font_size_heading_2', label: 'Titre niveau 2', min: 10, max: 16 },
                  { key: 'font_size_normal', label: 'Texte normal', min: 9, max: 14 },
                  { key: 'font_size_table', label: 'Texte tableau', min: 8, max: 12 },
                  { key: 'font_size_small', label: 'Texte petit', min: 7, max: 11 },
                  { key: 'font_size_footer', label: 'Pied de page', min: 6, max: 10 }
                ].map(({ key, label, min, max }) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-sm w-40">{label}</label>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <input
                      type="number"
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: parseInt(e.target.value) })}
                      className="w-16 p-1 border rounded text-center"
                      min={min}
                      max={max}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Couleurs */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
                🎨 Couleurs
              </h2>
              <div className="space-y-3">
                {[
                  { key: 'color_primary', label: 'Couleur principale (titres)' },
                  { key: 'color_secondary', label: 'Couleur secondaire (montants)' },
                  { key: 'color_text', label: 'Couleur texte principal' },
                  { key: 'color_text_light', label: 'Couleur texte secondaire' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-sm flex-1">{label}</label>
                    <input
                      type="color"
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: e.target.value })}
                      className="w-16 h-10 border rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: e.target.value })}
                      className="w-24 p-2 border rounded text-center font-mono text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Espacements */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
                📐 Espacements (pt)
              </h2>
              <div className="space-y-3">
                {[
                  { key: 'space_before_title', label: 'Avant titre principal', min: 0, max: 24 },
                  { key: 'space_after_title', label: 'Après titre principal', min: 0, max: 24 },
                  { key: 'space_before_heading', label: 'Avant titre section', min: 0, max: 24 },
                  { key: 'space_after_heading', label: 'Après titre section', min: 0, max: 24 },
                  { key: 'space_between_paragraphs', label: 'Entre paragraphes', min: 0, max: 18 }
                ].map(({ key, label, min, max }) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-sm w-48">{label}</label>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <input
                      type="number"
                      value={styles[key]}
                      onChange={(e) => setStyles({ ...styles, [key]: parseInt(e.target.value) })}
                      className="w-16 p-1 border rounded text-center"
                      min={min}
                      max={max}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Interligne */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
                📊 Interligne
              </h2>
              <div className="flex items-center gap-3">
                <label className="text-sm w-48">Interligne du texte</label>
                <input
                  type="range"
                  min="1.0"
                  max="2.0"
                  step="0.05"
                  value={styles.line_spacing}
                  onChange={(e) => setStyles({ ...styles, line_spacing: parseFloat(e.target.value) })}
                  className="flex-1"
                />
                <input
                  type="number"
                  value={styles.line_spacing}
                  onChange={(e) => setStyles({ ...styles, line_spacing: parseFloat(e.target.value) })}
                  className="w-20 p-1 border rounded text-center"
                  min="1.0"
                  max="2.0"
                  step="0.05"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                1.0 = Simple, 1.15 = Normal, 1.5 = Aéré, 2.0 = Double
              </p>
            </div>
          </div>

          {/* Prévisualisation */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye size={20} style={{ color: '#335C2E' }} />
                <h2 className="text-xl font-semibold" style={{ color: '#335C2E' }}>
                  Prévisualisation
                </h2>
              </div>

              {/* Preview Container */}
              <div className="border rounded-lg p-6 bg-white" style={{ 
                fontFamily: styles.font_family,
                fontSize: `${styles.font_size_normal}pt`,
                lineHeight: styles.line_spacing 
              }}>
                {/* Title */}
                <h1 
                  className="text-center font-bold"
                  style={{
                    fontFamily: styles.font_family_headings,
                    fontSize: `${styles.font_size_title}pt`,
                    color: styles.color_primary,
                    marginTop: `${styles.space_before_title}pt`,
                    marginBottom: `${styles.space_after_title}pt`
                  }}
                >
                  PRÉCONTRAT DE VENTE
                </h1>

                {/* Subtitle */}
                <p 
                  className="text-center italic"
                  style={{
                    fontSize: `${styles.font_size_small}pt`,
                    color: styles.color_text_light,
                    marginBottom: `${styles.space_between_paragraphs * 2}pt`
                  }}
                >
                  (Document officiel)
                </p>

                {/* Heading 1 */}
                <h2 
                  className="font-bold"
                  style={{
                    fontFamily: styles.font_family_headings,
                    fontSize: `${styles.font_size_heading_1}pt`,
                    color: styles.color_primary,
                    marginTop: `${styles.space_before_heading}pt`,
                    marginBottom: `${styles.space_after_heading}pt`
                  }}
                >
                  1. INFORMATIONS DU CLIENT
                </h2>

                {/* Normal text */}
                <p style={{ 
                  color: styles.color_text,
                  marginBottom: `${styles.space_between_paragraphs}pt`
                }}>
                  Ceci est un exemple de texte normal dans le document. La police, la taille et l'interligne sont appliqués selon votre configuration.
                </p>

                {/* Table preview */}
                <table className="w-full border-collapse" style={{ marginTop: `${styles.space_between_paragraphs}pt` }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F0F7F4' }}>
                      <th className="border p-2 text-left font-semibold" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_text 
                      }}>
                        Description
                      </th>
                      <th className="border p-2 text-right font-semibold" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_text 
                      }}>
                        Montant
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_text 
                      }}>
                        Sous-total
                      </td>
                      <td className="border p-2 text-right" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_text 
                      }}>
                        75,000.00 $
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-bold" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_text 
                      }}>
                        Total TTC
                      </td>
                      <td className="border p-2 text-right font-bold" style={{ 
                        fontSize: `${styles.font_size_table}pt`,
                        color: styles.color_secondary 
                      }}>
                        89,450.55 $
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Footer preview */}
                <div className="mt-8 pt-4 border-t text-center" style={{ 
                  fontSize: `${styles.font_size_footer}pt`,
                  color: styles.color_text_light 
                }}>
                  4000, montée Masson, Laval (Québec) • Page 1
                </div>
              </div>

              {/* Info box */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                <p className="font-semibold mb-1">💡 Astuce</p>
                <p className="text-gray-600">
                  Modifiez les paramètres à gauche et voyez immédiatement le résultat ici. 
                  Cliquez sur "Enregistrer" pour appliquer les changements à tous les futurs documents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentStyles;
